import React, {useContext, useEffect, useRef, useState} from "react";
import classNames from "classnames";
import {nanoid} from "nanoid/non-secure";
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import {arrayMove} from "@dnd-kit/sortable";
import ChevronRightIcon from "@heroicons/react/solid/ChevronRightIcon";
import MenuIcon from "@heroicons/react/solid/MenuIcon";
import FormContext from "../form-context";
import Form from "../index";
import {prefixFieldNames} from "../utils";
import _get from "lodash/get";
import axios from "axios";

const DragHandle = SortableHandle(() => (
    <div className="flex items-center cursor-pointer">
        <MenuIcon className="h-5 w-5 text-panels-700" aria-hidden="true"/>
    </div>
));

const SortableItem = SortableElement(({item, index}) => (
    <li
        className={classNames(
            "list-none bg-white flex items-center justify-between rounded border border-gray-500 shadow-button py-1 px-1.5",
            index > 0 && "mt-2"
        )}
    >
        <DragHandle/>
        <span className="grow text-sm font-semibold text-panels-700 text-left overflow-hidden text-ellipsis mx-2">
      {item.label || item.name || `Item ${index + 1}`}
    </span>
    </li>
));

const SortableList = SortableContainer(({items}) => (
    <ul className="sortable-list space-y-2">
        {items.map((item, idx) => (
            <SortableItem key={item.id} index={idx} item={item}/>
        ))}
    </ul>
));

export default function SchemaBindingConfig({
                                                id,
                                                name,
                                                label,
                                                description,
                                                className,
                                                editing = false,
                                                system = false,
                                                prefix,
                                                fieldsets = [],
                                                fileUploadSettings,
                                                buttonLabel,
                                                onFieldChange = () => {},
                                                onEditFieldSettings,
                                                labelFieldPath = "title",
                                                ...props
                                            }) {
    const context = useContext(FormContext);
    const containerRef = useRef();
    const value = _get(context.values, name)
    const listRef = useRef();
    const bindingConfig = value || {id: nanoid()};
    const [sortBy, setSortBy] = useState(value.sortBy ?? "default");
    const [items, setItems] = useState([]);

    const onItemsChange = (items) => {
        onFieldChange(null, `${name}.order`, items);
    };

    useEffect(() => {
        const cancelToken = axios.CancelToken.source();

        if (context?.values?.values?.schema && context?.selectedNode?.channel?.code) {
            axios
                .get(
                    `/api/v2/channels/${context.selectedNode.channel.code}/nodes/${context.values.values.schema}/`,
                    {cancelToken: cancelToken.token}
                )
                .then(({data}) => {
                    const children = data?.[0]?.children || [];

                    // Map API result into {id, label} objects
                    const mapped = children.map((node) => ({
                        id: node?.uuid,
                        label: _get(node, labelFieldPath) || _get(node, "title", "") || "",
                    }));

                    // Grab the saved order array (if it exists) from form values.
                    const savedOrder = value?.order ?? [];

                    // Extract just the IDs from savedOrder
                    const savedIds = savedOrder.map((item) => item.id);

                    // Keep only those saved items whose IDs still appear in this fetch
                    const existingSavedItems = savedOrder.filter((item) =>
                        mapped.some((m) => m.id === item.id)
                    );

                    // Find any new items (ones in `mapped` but not in savedIds)
                    const newItems = mapped.filter((item) => !savedIds.includes(item.id));

                    // Final ordered array: first the saved (filtered), then any new ones
                    const ordered = [...existingSavedItems, ...newItems];

                    setItems(ordered);
                    onItemsChange(ordered);
                })
                .catch((err) => {
                    if (!axios.isCancel(err)) console.log(err);
                });
        }

        return () => cancelToken.cancel();
    }, [
        context?.values?.values?.schema,
        context?.selectedNode?.channel?.code,
        labelFieldPath,
        name,
    ]);

    const openBindingEditor = () => {
        const prefixedFieldsets = prefixFieldNames(fieldsets, prefix || name);
        const uploadSettings =
            typeof fileUploadSettings === "function"
                ? fileUploadSettings(bindingConfig)
                : fileUploadSettings;

        context.setContext({
            showChildForm: true,
            childForm: (
                <Form
                    id="schema_binding_editor"
                    layout="panel-style"
                    className="h-full"
                    heading={label || "Configure Binding"}
                    fieldsets={prefixedFieldsets}
                    parentContext={context}
                    selectedNode={context.selectedNode}
                    values={context.values}
                    original={context.original}
                    fileUploadSettings={uploadSettings || context.fileUploadSettings}
                    actions={[
                        {label: "Cancel", back: true},
                        {label: "Done", primary: true, icon: "check"},
                    ]}
                    onSubmit={(e, values) => {
                        const updatedConfig = values[name];
                        onFieldChange(e, name, updatedConfig);
                        context.setContext({showChildForm: false});
                    }}
                    onActionClick={(e, action) => {
                        if (action.back) context.setContext({showChildForm: false});
                    }}
                    onFieldChange={(e, fieldName, fieldValue) => onFieldChange(e, fieldName, fieldValue)}
                />
            ),
        });
    };

    return (
        <>
            <div className={classNames("relative", className)}>
                <div className="rounded border border-gray-500 shadow-button px-3 pt-2 pb-1.5 grow">
                    <div className="flex mb-1 justify-between">
                        <label htmlFor={id || name} className="block text-sm font-semibold text-gray-700">
                            {label || "Configure Binding"}
                        </label>
                    </div>
                    <div className="relative">
                        <div
                            className="absolute top-0 left-0 right-0 h-[40px] -mt-[33px] flex items-center justify-between cursor-pointer"
                            onClick={openBindingEditor}
                        >
                            <span className="grow"/>
                            <ChevronRightIcon className="w-5 h-5 shrink-0 text-gray-500"/>
                        </div>
                    </div>
                </div>
            </div>

            <div className={classNames("grow control-select-item-action-style", className, "mb-4")}>
                <div className="flex mb-1 justify-between">
                    <label
                        htmlFor={`${name}.sortBy`}
                        className="block text-sm font-semibold text-gray-700">
                        Sort By
                    </label>
                </div>
                <div className="w-full relative">
                    <select
                        id={`${name}.sortBy`}
                        name={`${name}.sortBy`}
                        autoComplete="off"
                        autoCapitalize="off"
                        className="px-1.5 py-1 shadow-sm block w-full text-sm rounded focus:ring-primary-500 focus:border-primary-500 border-gray-300 pr-6"
                        value={sortBy}
                        onChange={(e) => {
                            const value = e.target.value;
                            onFieldChange(e, `${name}.sortBy`, value);
                            setSortBy(value)
                        }}>
                        <option label="Custom Sort" value="custom">
                            Custom Sort
                        </option>
                        <option label="Default Sort" value="default">
                            Default Sort
                        </option>
                    </select>
                </div>
            </div>

            <div ref={containerRef} className={classNames("relative", className)}>
                {sortBy === "custom" && items.length > 0 && (
                    <SortableList
                        ref={listRef}
                        items={items}
                        useDragHandle
                        helperClass="sort-helper"
                        getContainer={() => {
                            let parent = containerRef.current.parentElement;
                            while (
                                parent &&
                                !(parent.tagName === "BODY" || parent.scrollHeight > parent.offsetHeight)
                                )
                                parent = parent.parentElement;
                            return parent || document.body;
                        }}
                        onSortStart={() => {
                            const helper = document.getElementsByClassName("sort-helper")[0];
                            if (helper) helper.style.zIndex = 50000;
                        }}
                        onSortEnd={({oldIndex, newIndex}) => {
                            const newItems = arrayMove(items, oldIndex, newIndex);
                            setItems(newItems);
                            onItemsChange(newItems);
                        }}
                    />
                )}
            </div>
        </>
    );
}